import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import { Alert, Button, Grid, Icon, Typography } from 'gu-ui-kit'
import CreateAgencyForm from 'modules/Forms/Agency/Create'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import formatPhone from 'utils/formatPhone'
import styles from 'modules/Agency/AgencyCard/agency-card.module.scss'
import PageHeader from 'components/PageHeader'
import { notificationShow } from 'modules/Notifications/actions'
import useWriteToClipboard from 'utils/hooks/useWriteToClipboard'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const AgencyCard = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['agency', 'common'])
  const { loading, loadFailed, id, name, email, phone } = useSelector(({ agency }) => agency)
  const writeToClipboard = useWriteToClipboard()
  const breakpoint = useBreakpoint()
  useEffect(() => {
    dispatch({ type: 'AGENCY/LOAD_DATA' })
  }, [])

  const toClipboard = useCallback((text) => {
    writeToClipboard(text)

    dispatch(
      notificationShow({
        type: 'success',
        message: 'common::notifications.success_copy_notification',
      }),
    )
  }, [])

  if (loading) {
    return (
      <div className={clsx('layout-tile', styles['agency-card'])}>
        <GridSpinner />
      </div>
    )
  }

  if (loadFailed) {
    return (
      <Alert
        variant="filled"
        severity="error"
      >
        {t('common::server_request_error')}
      </Alert>
    )
  }

  return !loading && !id ? (
    <CreateAgencyForm />
  ) : (
    <div className={styles['agency-card']}>
      <div className={'layout-tile margin-bottom-16'}>
        <PageHeader>{t('my_agency.title')}</PageHeader>
      </div>

      <div className={'layout-tile'}>
        <Grid
          direction={'column'}
          gap={40}
        >
          <Grid
            gap={16}
            alignItems="center"
          >
            <Typography
              variant={'Subtitle-30'}
              color={'Label-Onsurface'}
              component={
                <Grid
                  alignItems={'center'}
                  justifyContent={'center'}
                  fluid={false}
                  className={clsx(styles['agency-card__avatar'], 'bg-network-admitad')}
                />
              }
            >
              Ad
            </Typography>
            <Typography
              variant={'Subtitle-30'}
              color={'Label-Fifth'}
              component={'div'}
            >
              {name}
            </Typography>
          </Grid>
          <Grid
            gap={16}
            wrap={false}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Grid
              alignItems={'center'}
              justifyContent={'center'}
              className={styles['agency-card__icon']}
              fluid={false}
            >
              <Icon
                name={'mail'}
                color={'Interface-Eighth'}
                width={20}
              />
            </Grid>
            <Grid
              gap={4}
              direction={'column'}
            >
              <Typography
                variant={'Body-10'}
                color={'Label-Secondary'}
              >
                {t('my_agency.email_label')}:
              </Typography>
              <Typography
                variant={'Body-15'}
                color={'Label-Fifth'}
              >
                {email || '—'}
              </Typography>
            </Grid>
            <Button
              variant={'ghost'}
              size={'s'}
              grey
              onClick={() => toClipboard(email || '—')}
              noLabel={!breakpoint.sm}
              startIcon={
                <Icon
                  name={'copy'}
                  color={'Interface-Eighth'}
                />
              }
            >
              {t('common::buttons.copy')}
            </Button>
          </Grid>

          <Grid
            gap={16}
            wrap={false}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Grid
              alignItems={'center'}
              justifyContent={'center'}
              className={styles['agency-card__icon']}
              fluid={false}
            >
              <Icon
                name={'phone'}
                color={'Interface-Eighth'}
                width={20}
              />
            </Grid>
            <Grid
              gap={4}
              direction={'column'}
            >
              <Typography
                variant={'Body-10'}
                color={'Label-Secondary'}
              >
                {t('my_agency.phone_label')}:
              </Typography>
              <Typography
                variant={'Body-15'}
                color={'Label-Fifth'}
              >
                {formatPhone(phone) || '—'}
              </Typography>
            </Grid>
            <Button
              variant={'ghost'}
              size={'s'}
              grey
              onClick={() => toClipboard(formatPhone(phone) || '—')}
              noLabel={!breakpoint.sm}
              startIcon={
                <Icon
                  name={'copy'}
                  color={'Interface-Eighth'}
                />
              }
            >
              {t('common::buttons.copy')}
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default AgencyCard
